// The edge calls behind continuing a tasting on another device (apps/edge/src/tastingDrafts.ts), and
// the two things the outbox does with them: carry the latest copy of a tasting, and drop it.
// Nothing here runs unless the person turned keeping tastings on (draftSync.ts decides that): the
// edge refuses a write without that consent as well.
import {
  OkResponse,
  PatchTastingDraftSettingsRequest,
  PutTastingDraftRequest,
  PutTastingDraftResponse,
  TastingDraftList,
  TastingDraftSettings,
  TastingRunSnapshot,
} from "@proef/contract";
import { settleDraft } from "@proef/domain";
import { call } from "../edgeApi";
import { pushNotice } from "./draftNotices";
import type { TastingRun } from "./run";
import { adoptRun, getAllRuns, getRun } from "./store";

export function getDraftSettings(): Promise<TastingDraftSettings> {
  return call(TastingDraftSettings, "/v1/tasting-drafts/settings");
}

export function patchDraftSettings(enabled: boolean): Promise<TastingDraftSettings> {
  return call(TastingDraftSettings, "/v1/tasting-drafts/settings", {
    method: "PATCH",
    body: JSON.stringify(PatchTastingDraftSettingsRequest.parse({ enabled })),
  });
}

export function listDrafts(): Promise<TastingDraftList> {
  return call(TastingDraftList, "/v1/tasting-drafts");
}

function putDraft(snapshot: TastingRunSnapshot): Promise<PutTastingDraftResponse> {
  return call(PutTastingDraftResponse, `/v1/tasting-drafts/${encodeURIComponent(snapshot.id)}`, {
    method: "PUT",
    body: JSON.stringify(PutTastingDraftRequest.parse({ snapshot })),
  });
}

function deleteDraft(runId: string): Promise<OkResponse> {
  return call(OkResponse, `/v1/tasting-drafts/${encodeURIComponent(runId)}`, { method: "DELETE" });
}

/** The stamps this device itself wrote, so a refusal that names one of them is not another device. */
const sent = new Map<string, string[]>();
const KEEP_STAMPS = 8;
/** The `updatedAt` last carried per tasting, for "has anything changed since". */
export const lastCarried = new Map<string, string>();

function remember(runId: string, stamp: string): void {
  sent.set(runId, [...(sent.get(runId) ?? []), stamp].slice(-KEEP_STAMPS));
  lastCarried.set(runId, stamp);
}

/** A copy of the tasting as it goes to the edge: the account is named even if it began signed out. */
function snapshotOf(run: TastingRun, userId: string): TastingRunSnapshot | undefined {
  const parsed = TastingRunSnapshot.safeParse({ ...run, ownerId: userId });
  return parsed.success ? parsed.data : undefined;
}

/**
 * Another device wrote a newer copy: take it, unless this device has moved on since, and say so
 * once. The copy of a tasting held here is replaced in place, wherever it sits.
 */
async function settleWithRemote(runId: string): Promise<void> {
  const { items } = await listDrafts();
  const remote = items.find((item) => item.runId === runId);
  const local = getAllRuns().find((run) => run.id === runId);
  if (remote === undefined || local === undefined) return;
  const verdict = settleDraft(local, remote);
  if (verdict.winner !== "remote") return;
  adoptRun(remote.snapshot, { active: getRun()?.id === runId });
  lastCarried.set(runId, remote.updatedAt);
  if (verdict.replaced) pushNotice({ runId, title: remote.snapshot.title });
}

/** The outbox action: carries the tasting's latest copy to the edge, as the person it belongs to. */
export async function sendDraft(userId: string, runId: string): Promise<void> {
  const run = getAllRuns().find((held) => held.id === runId);
  if (run === undefined || (run.ownerId !== undefined && run.ownerId !== userId)) return;
  const snapshot = snapshotOf(run, userId);
  if (snapshot === undefined) return;
  const answer = await putDraft(snapshot);
  if (answer.accepted) {
    remember(runId, snapshot.updatedAt);
    return;
  }
  if (sent.get(runId)?.includes(answer.updatedAt) === true) return;
  await settleWithRemote(runId);
}

/** The outbox action for a tasting that ended or was thrown away here: its kept copy goes too. */
export async function dropDraft(runId: string): Promise<void> {
  sent.delete(runId);
  lastCarried.delete(runId);
  await deleteDraft(runId);
}
